// src/views/networking/ConnectionHistory.tsx
import { Flex, Text, JWTRole } from "@hackthenorth/north";
import moment from "moment";
import {
  Card,
  Checkbox,
  DropdownMenu,
  RadioButton,
  Spinner,
  Label,
} from "north.js";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { Icon } from "src/shared/components";
import Modal from "src/shared/components/Modal";
import { getBadgeColor } from "src/shared/components/Sidebar";

import LinkTreeView from "../networkingResult/LinkTreeView";

import { useFetchNetworkingScanLazyQuery } from "./graphql/fetchNetworkingScan.generated";
import { useGetUserNetworkingScansQuery } from "./graphql/getUserNetworkingScans.generated";

const ConnectionHistory: React.FC = () => {
  const { data, loading, refetch } = useGetUserNetworkingScansQuery();
  const [filter, setFilter] = useState<string[]>([]);
  const [directionFilter, setDirectionFilter] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [
    fetchNetworkingScan,
    { data: fetchScanData, loading: fetchScanLoading },
  ] = useFetchNetworkingScanLazyQuery({ notifyOnNetworkStatusChange: true });

  // periodically refetch scans
  useEffect(() => {
    const id = setInterval(refetch, 300000);
    return () => clearInterval(id);
  }, [refetch]);

  // filter helpers
  const toggleFilter = (category: string) => {
    setFilter((prev) =>
      prev.includes(category)
        ? prev.filter((cat) => cat !== category)
        : [...prev, category]
    );
  };

  const toggleDirection = (dir: string) => {
    setDirectionFilter((prev) => (prev === dir ? "" : dir));
  };

  // role filters
  const rolesfilter = [
    [
      {
        content: (
          <Checkbox
            checked={filter.includes("hacker")}
            onChange={() => toggleFilter("hacker")}
            label="Hacker"
          />
        ),
        action: () => {},
      },
      {
        content: (
          <Checkbox
            checked={filter.includes("volunteer")}
            onChange={() => toggleFilter("volunteer")}
            label="Volunteer"
          />
        ),
        action: () => {},
      },
      {
        content: (
          <Checkbox
            checked={filter.includes("mentor")}
            onChange={() => toggleFilter("mentor")}
            label="Mentor"
          />
        ),
        action: () => {},
      },
      {
        content: (
          <Checkbox
            checked={filter.includes("workshop_lead")}
            onChange={() => toggleFilter("workshop_lead")}
            label="Workshop Lead"
          />
        ),
        action: () => {},
      },
      {
        content: (
          <Checkbox
            checked={filter.includes("organizer")}
            onChange={() => toggleFilter("organizer")}
            label="Organizer"
          />
        ),
        action: () => {},
      },
      {
        content: (
          <Checkbox
            checked={filter.includes("sponsor")}
            onChange={() => toggleFilter("sponsor")}
            label="Sponsor"
          />
        ),
        action: () => {},
      },
    ],
  ];

  // direction filters
  const directions = [
    [
      {
        content: (
          <RadioButton
            checked={directionFilter === "You scanned"}
            name="direction"
            onChange={() => toggleDirection("You scanned")}
            label="You scanned"
          />
        ),
        action: () => {},
      },
      {
        content: (
          <RadioButton
            checked={directionFilter === "Scanned you"}
            name="direction"
            onChange={() => toggleDirection("Scanned you")}
            label="Scanned you"
          />
        ),
        action: () => {},
      },
    ],
  ];

  // filtered scans
  const scans = data?.getUserNetworkingScans
    ?.filter((scan) => !scan.is_duplicate)
    ?.filter((scan) =>
      filter.length && scan.other_attendee_role
        ? filter.includes(scan.other_attendee_role)
        : true
    )
    ?.filter((scan) =>
      directionFilter
        ? (directionFilter === "Scanned you" && scan.got_scanned) ||
          (directionFilter === "You scanned" && !scan.got_scanned)
        : true
    );

  return (
    <ConnectionWrapper>
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="">
        {fetchScanLoading && <Spinner />}
        {!fetchScanLoading && (
          <>
            {fetchScanData?.fetchNetworkingScan.hackerNetworkingPayload ? (
              <LinkTreeView
                {...fetchScanData.fetchNetworkingScan.hackerNetworkingPayload}
              />
            ) : (
              <EmptyStateWrapper>
                <Text mods="heading h3">
                  This person has no networking information
                </Text>
              </EmptyStateWrapper>
            )}
          </>
        )}
      </Modal>

      <ConnectionHeader>
        <Text mods="heading h3 bold">Connection history</Text>
        <Text>
          People you&apos;ve connected with during Hack the North 2026
        </Text>
        <Filters>
          <StyledDropdown items={rolesfilter}>
            <FilterCard>
              <Text mods="heading h4 lighter" tw="mt-0! leading-normal!">
                Filter by category
              </Text>
              <Icon name="filter" size="h3" />
            </FilterCard>
          </StyledDropdown>
          <StyledDropdown items={directions}>
            <FilterCard>
              <Icon
                name={directionFilter ? "radio-selected" : "radio-empty"}
                size="h3"
              />
              <Text mods="heading h4 lighter" tw="mt-0! leading-normal!">
                {directionFilter || "You scanned"}
              </Text>
            </FilterCard>
          </StyledDropdown>
        </Filters>
      </ConnectionHeader>

      {loading && <Spinner />}
      {scans?.map((scan, idx) => (
        <LinkCard
          key={idx}
          onClick={() => {
            if (scan.other_attendee_role !== "sponsor") {
              fetchNetworkingScan({ variables: { badge_scan_id: scan.id } });
              setShowModal(true);
            }
          }}
        >
          <LinkCardBody>
            <Flex>
              <Text mods="bold">
                {scan.other_attendee_name}
                {scan.other_attendee_role === "sponsor" &&
                  ` (${scan.sponsor_scan?.company_name})`}
              </Text>
              {scan.other_attendee_role && (
                <RoleLabel
                  color={getBadgeColor(scan.other_attendee_role as JWTRole)}
                  size="sm"
                >
                  {scan.other_attendee_role}
                </RoleLabel>
              )}
            </Flex>
            <Text>{moment(scan.scanned_at).format("MMMM Do, h:mm A")}</Text>
          </LinkCardBody>
          {scan.other_attendee_role !== "sponsor" && (
            <Icon name="external-link" size="h3" />
          )}
        </LinkCard>
      ))}
    </ConnectionWrapper>
  );
};

export default ConnectionHistory;

// ----------------- styles -----------------
const ConnectionWrapper = styled.div`
  margin-top: 42px;
`;

const ConnectionHeader = styled.div`
  margin-bottom: 16px;
`;

const Filters = styled.div`
  display: flex;
  max-width: 380px;
  justify-content: space-between;
`;

const StyledDropdown = styled(DropdownMenu)`
  border: 1px solid #d1d5db;
  box-shadow: 0px 4px 6px -1px rgba(0, 0, 0, 0.1),
    0px 2px 4px -1px rgba(0, 0, 0, 0.06);
  border-radius: 8px !important;
  border-top-right-radius: 0px !important;
  border-top-left-radius: 0px !important;
  border-top-width: 0;
  left: 0;
  width: 100% !important;
  top: -30px;
`;

const FilterCard = styled(Card)`
  display: flex;
  align-items: center;
  margin: 32px 0 16px 0;
  padding: 12px 16px !important;
  border: 1px solid #d1d5db !important;
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06) !important;
  border-radius: 8px !important;
  gap: 12px;
  p {
    font-weight: 300 !important;
    margin-bottom: 0 !important;
  }
`;

const LinkCard = styled(Card)`
  margin: 16px 0;
  padding: 16px 24px !important;
  border: 1px solid #d1d5db !important;
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06) !important;
  border-radius: 8px !important;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LinkCardBody = styled.div`
  max-width: 85%;
  display: flex;
  flex-direction: column;
  p {
    overflow-wrap: break-word;
  }
`;

const RoleLabel = styled(Label)`
  height: fit-content;
  margin-left: 8px;
`;

const EmptyStateWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  min-height: 200px;
`;
